import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import type {
  AvailableTransportIngredientResponseWithStaticInfo,
  DayTicketRate,
  DestinationStaticInfo,
  DetailsStaticInfo,
  Dictionary,
  DynamicDetailedDestinationProduct,
  HotelBookableAndLockableOption,
  IngredientId,
  OverviewDestinationStaticInfo,
  StaticDetailedDestinationProduct,
} from '@libs/trip-planner-interfaces';
import { ProductSearchType } from '@libs/trip-planner-interfaces';
import { getHotelKey, isAccommodation, readonlyRef } from '@libs/utils';

import { useItineraryStore } from '../itinerary/itineraryStore';
import { useRecipeStore } from '../recipe/recipeStore';
import { useTripStore } from '../trip/tripStore';

export const useProductDetailStore = defineStore('ProductDetailStore', () => {
  const itineraryStore = useItineraryStore();
  const tripStore = useTripStore();
  const recipeStore = useRecipeStore();

  // State
  const destinationProductDetailCode = ref<string>();
  const destinationProductDetailIngredientId = ref<IngredientId>();
  const productSearchType = ref<ProductSearchType | undefined>(undefined);

  const destinationProductDetailStaticInfo = ref<DetailsStaticInfo>();

  const cachedProductCode = ref<string | undefined>();
  const cachedIngredientId = ref<IngredientId | undefined>();
  const cachedStaticInfo = ref<StaticDetailedDestinationProduct | undefined>();

  const accommodationDetailAvailabilityLoadingCounter = ref<number>(0);
  const accommodationDetailAvailability = ref<
    Dictionary<HotelBookableAndLockableOption>
  >({});

  const activityDetailAvailability = ref<DayTicketRate[]>([]);

  const transportProductDetail = ref<
    AvailableTransportIngredientResponseWithStaticInfo | undefined
  >();

  const shouldScrollToAvailability = ref<boolean>(false);

  const accommodationDetailAvailabilityLoading = computed<boolean>(
    () => accommodationDetailAvailabilityLoadingCounter.value > 0
  );

  const destinationStaticProductDetailForHotel = computed<
    StaticDetailedDestinationProduct | undefined
  >(() => {
    const productCode = destinationProductDetailCode.value;
    const ingredientId = destinationProductDetailIngredientId.value;

    if (
      !productCode ||
      !ingredientId ||
      !productSearchType.value ||
      productSearchType.value === ProductSearchType.Activity
    ) {
      return;
    }

    const ingredientResponse =
      itineraryStore.getHotelIngredientResponse(ingredientId) ||
      itineraryStore.getOwnStayIngredientResponse(ingredientId);

    const staticInfo =
      destinationProductDetailStaticInfo.value?.hotelCode === productCode
        ? destinationProductDetailStaticInfo.value
        : tripStore.getStaticInfo(productCode, ingredientId);

    if (!staticInfo || !ingredientResponse) {
      if (
        productCode === cachedProductCode.value &&
        ingredientId === cachedIngredientId.value &&
        cachedStaticInfo.value
      ) {
        return {
          ...cachedStaticInfo.value,
          isAvailabilityLoading: accommodationDetailAvailabilityLoading.value,
        };
      }
      return;
    }

    const isHearted =
      isAccommodation(staticInfo.typeOfHotel) &&
      tripStore.hotelIsHearted(productCode, ingredientId);

    const isInItinerary =
      isAccommodation(staticInfo.typeOfHotel) &&
      tripStore.isHotelInItinerary(ingredientId, productCode);
    const numberOfNights = ingredientResponse.numberOfNights;
    const result: StaticDetailedDestinationProduct = {
      ingredientId,
      staticInfo,
      productCode,
      isHearted,
      isInItinerary,
      numberOfNights,
      isAvailabilityLoading: accommodationDetailAvailabilityLoading.value,
    };

    cachedProductCode.value = productCode;
    cachedIngredientId.value = ingredientId;
    cachedStaticInfo.value = result;
    return result;
  });

  const destinationDynamicProductDetailForHotel = computed<
    DynamicDetailedDestinationProduct | undefined
  >(() => {
    if (
      !productSearchType.value ||
      productSearchType.value === ProductSearchType.Activity
    ) {
      return;
    }

    return {
      availability: Object.values(accommodationDetailAvailability.value),
    };
  });

  const destinationStaticProductDetailForActivity = computed<
    StaticDetailedDestinationProduct | undefined
  >(() => {
    const productCode = destinationProductDetailCode.value;
    const ingredientId = destinationProductDetailIngredientId.value;

    if (
      !productCode ||
      !ingredientId ||
      !productSearchType.value ||
      productSearchType.value !== ProductSearchType.Activity
    ) {
      return;
    }

    const ingredientResponse =
      itineraryStore.getHotelIngredientResponse(ingredientId) ||
      itineraryStore.getOwnStayIngredientResponse(ingredientId);

    if (!ingredientResponse) {
      return;
    }

    const parentIngredientId = ingredientResponse.ingredientId;
    const staticInfo =
      destinationProductDetailStaticInfo.value?.hotelCode === productCode
        ? destinationProductDetailStaticInfo.value
        : tripStore.getStaticInfo(productCode, parentIngredientId);

    if (!staticInfo) {
      return;
    }

    const isHearted =
      isAccommodation(staticInfo.typeOfHotel) &&
      tripStore.hotelIsHearted(productCode, ingredientId);

    const isInItinerary =
      isAccommodation(staticInfo.typeOfHotel) &&
      tripStore.isHotelInItinerary(ingredientId, productCode);
    const numberOfNights = ingredientResponse.numberOfNights;

    return {
      ingredientId: parentIngredientId,
      staticInfo,
      productCode,
      isHearted,
      isInItinerary,
      numberOfNights,
      isAvailabilityLoading: accommodationDetailAvailabilityLoading.value,
    };
  });

  const destinationDynamicProductDetailForActivity = computed<
    { availability: DayTicketRate[] } | undefined
  >(() => {
    if (
      !productSearchType.value ||
      productSearchType.value !== ProductSearchType.Activity
    ) {
      return;
    }

    return {
      availability: activityDetailAvailability.value,
    };
  });

  watch(destinationStaticProductDetailForActivity, () => {
    recipeStore.updateActivityTickets(
      destinationStaticProductDetailForActivity.value
        ? recipeStore.getExistingSelectedTickets(
            destinationStaticProductDetailForActivity.value.productCode,
            destinationStaticProductDetailForActivity.value.ingredientId
          )
        : []
    );
  });

  function isDetailStaticInfo(
    staticInfo:
      | OverviewDestinationStaticInfo
      | DestinationStaticInfo
      | DetailsStaticInfo
  ): staticInfo is DetailsStaticInfo {
    return 'distances' in staticInfo && 'properties' in staticInfo;
  }

  function selectDestinationProduct(
    hotelCode: string,
    ingredientId: IngredientId,
    type: ProductSearchType
  ): void {
    clearTransportProductDetails();
    if (
      destinationProductDetailCode.value === hotelCode &&
      destinationProductDetailIngredientId.value === ingredientId &&
      productSearchType.value === type
    ) {
      return;
    }

    destinationProductDetailCode.value = hotelCode;
    destinationProductDetailIngredientId.value = ingredientId;
    productSearchType.value = type;
    accommodationDetailAvailability.value = {};
    activityDetailAvailability.value = [];
  }

  function clearDestinationProductDetailAvailability(): void {
    accommodationDetailAvailability.value = {};
    activityDetailAvailability.value = [];
  }

  function clearDestinationProductDetailStaticInfo(): void {
    destinationProductDetailStaticInfo.value = undefined;
  }

  function clearTransportProductDetails(): void {
    transportProductDetail.value = undefined;
  }

  function clearDestinationProductDetails(): void {
    destinationProductDetailCode.value = undefined;
    destinationProductDetailIngredientId.value = undefined;
    productSearchType.value = undefined;
    accommodationDetailAvailability.value = {};
    activityDetailAvailability.value = [];
  }

  function clearProductDetails(): Promise<void> {
    clearDestinationProductDetails();
    clearTransportProductDetails();
    return Promise.resolve();
  }

  function updateShouldScrollToAvailability(should: boolean): void {
    shouldScrollToAvailability.value = should;
  }

  function setDestinationProductDetailStaticInfo(
    detailsStaticInfo: DetailsStaticInfo
  ): void {
    destinationProductDetailStaticInfo.value = detailsStaticInfo;
    tripStore.insertOverviewStaticInfo({
      destinationStaticInfo: {},
      subIngredientsStaticInfo: {},
      heartedHotelsStaticInfo: {
        [getHotelKey(
          detailsStaticInfo.hotelCode,
          detailsStaticInfo.ingredientId
        )]: detailsStaticInfo,
      },
    });
  }

  function incrementAccommodationDetailAvailabilityLoadingCounter(): void {
    accommodationDetailAvailabilityLoadingCounter.value += 1;
  }

  function decrementAccommodationDetailAvailabilityLoadingCounter(): void {
    accommodationDetailAvailabilityLoadingCounter.value -= 1;
  }

  function updateAccommodationDetailAvailability(
    uniqueKey: string,
    value: HotelBookableAndLockableOption
  ): void {
    accommodationDetailAvailability.value[uniqueKey] = value;
  }

  function setActivityDetailAvailability(value: DayTicketRate[]): void {
    activityDetailAvailability.value = value;
  }

  return {
    // State
    shouldScrollToAvailability: readonlyRef(shouldScrollToAvailability),
    destinationProductDetailCode: readonlyRef(destinationProductDetailCode),
    destinationProductDetailIngredientId: readonlyRef(
      destinationProductDetailIngredientId
    ),
    productSearchType: readonlyRef(productSearchType),

    destinationStaticProductDetailForHotel,
    destinationDynamicProductDetailForHotel,
    destinationStaticProductDetailForActivity,
    destinationDynamicProductDetailForActivity,

    // Actions and mutations
    updateAccommodationDetailAvailability,
    selectDestinationProduct,
    isDetailStaticInfo,
    clearProductDetails,
    clearDestinationProductDetailAvailability,
    clearDestinationProductDetailStaticInfo,
    setDestinationProductDetailStaticInfo,
    setActivityDetailAvailability,
    updateShouldScrollToAvailability,
    incrementAccommodationDetailAvailabilityLoadingCounter,
    decrementAccommodationDetailAvailabilityLoadingCounter,
  };
});
